import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import PropTypes from 'prop-types';

import MobileTabsContext from './MobileTabsContext';
import { getRememberedLocation, resolveTabId, upsertPane } from './MobileTabsProvider.utils';

/**
 * Owns the native bottom-nav tab stacks. On every browser-history change it resolves the owning tab
 * for the new location (a tab tap can pre-declare it via `setPendingTabId`; otherwise it is derived
 * from the path, with deep loan-editor routes sticking to the active tab) and updates that tab's
 * kept-alive pane to the live location. Every other tab keeps its frozen location, so its subtree
 * stays mounted — preserving route position and scroll across tab switches. The single shared
 * browser history is never replaced, so all existing navigation (incl. the singleton `@history`)
 * keeps working.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
const MobileTabsProvider = ({ children }) => {
  const location = useLocation();

  const bootstrapRef = useRef(null);

  if (!bootstrapRef.current) {
    bootstrapRef.current = {
      tabId: resolveTabId(location.pathname, null),
      location,
    };
  }

  const { tabId: initialTabId, location: initialLocation } = bootstrapRef.current;

  const [panes, setPanes] = useState(() => [{ tabId: initialTabId, location: initialLocation }]);
  const [activeTabId, setActiveTabId] = useState(initialTabId);

  const activeTabIdRef = useRef(initialTabId);
  const lastLocationByTabRef = useRef({ [initialTabId]: initialLocation });
  const pendingTabIdRef = useRef(null);

  const setPendingTabId = useCallback(tabId => {
    pendingTabIdRef.current = tabId;
  }, []);

  const getTabLocation = useCallback(
    tabId => getRememberedLocation(lastLocationByTabRef.current, tabId),
    [],
  );

  useEffect(() => {
    const pending = pendingTabIdRef.current;
    pendingTabIdRef.current = null;

    const tabId = pending ?? resolveTabId(location.pathname, activeTabIdRef.current);

    activeTabIdRef.current = tabId;
    lastLocationByTabRef.current = { ...lastLocationByTabRef.current, [tabId]: location };

    setActiveTabId(tabId);
    setPanes(prev => upsertPane(prev, tabId, location));
  }, [location]);

  const value = useMemo(
    () => ({ panes, activeTabId, getTabLocation, setPendingTabId }),
    [panes, activeTabId, getTabLocation, setPendingTabId],
  );

  return <MobileTabsContext.Provider value={value}>{children}</MobileTabsContext.Provider>;
};

MobileTabsProvider.propTypes = {
  children: PropTypes.node,
};

export default MobileTabsProvider;
